'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { useMemo, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Form from '@nineeng/forms';
import {
	GenericDetailPage,
	Tabs,
	Spinner,
	MissingParentRolesAlert,
	TagAutocomplete,
} from '@repo/components';
import { Alert } from '@mui/material';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import {
	Type,
	Role,
	Requirement,
	Tag,
	FeatureFlagsSettings,
	FormsApiResponse,
	TaskTemplatesApiResponse,
} from '@repo/shared/domain';
import { EventEmitter, constructQuery, guardedClient as clientRequest } from '@repo/shared/helpers';
import { useConfig } from 'src/hooks/useConfig';
import { getTypeForModel } from 'src/api/admin/types';
import { getForms } from 'src/api/admin/forms';
import { getTaskTemplates } from 'src/api/admin/taskTemplates';
import { getRequirements } from 'src/api/admin/types';
import { getAllRoles } from 'src/api/admin/roles';
import { getAllTags } from 'src/api/admin/tags';

export const Route = createFileRoute('/_admin/config/types/event/$typeId')({
	component: EventTypeDetailPage,
});

// Tab Components
interface GeneralTabProps {
	data: Type & { tagIds?: string[] };
	onChange: (_data: Type & { tagIds?: string[] }) => void;
	forms: FormsApiResponse['data'];
	tags?: Tag[];
	tagsLoading?: boolean;
	revalidateTags?: () => void;
	featureFlags?: FeatureFlagsSettings['settings'];
}

function GeneralTab({ data, onChange, forms, tags = [], tagsLoading = false, revalidateTags, featureFlags }: GeneralTabProps) {
	const [selectedTagIds, setSelectedTagIds] = useState<string[]>(data?.tagIds || []);
	const [allTags, setAllTags] = useState<Tag[]>(tags);

	useEffect(() => {
		setAllTags((prev) => {
			const newlyCreated = prev.filter((tag) => !tags.some((t) => String(t.id) === String(tag.id)));
			const merged = [...tags, ...newlyCreated];
			return merged.filter((tag, index, self) => index === self.findIndex((t) => String(t.id) === String(tag.id)));
		});
	}, [tags]);

	useEffect(() => {
		setSelectedTagIds(data?.tagIds || []);
	}, [data?.tagIds]);

	const handleReloadTags = async (createdTags?: Tag[]) => {
		if (createdTags && createdTags.length > 0) {
			setAllTags((prev) => [...prev, ...createdTags]);
		}
		if (revalidateTags) {
			revalidateTags();
		}
	};

	const handleTagChange = (event: { target: { value: string | string[] | null } }) => {
		const newValue = Array.isArray(event.target.value)
			? event.target.value
			: event.target.value
				? [event.target.value]
				: [];
		setSelectedTagIds(newValue);
		onChange({ ...data, tagIds: newValue });
	};

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Type Name',
						name: 'name',
						type: 'TextField',
						required: true,
					},
					{
						rowLabel: 'Form',
						rowDescription: 'Properties will be managed using the assigned form',
						name: 'form',
						type: 'AutoCompleteByID',
						options: forms.map((t) => ({ ...t, id: t.slug })),
						getOptionLabel: (o: any) => o.title,
					},
					featureFlags?.USES_TAGS && {
						rowLabel: 'Event Type Labels',
						rowDescription: 'Labels to categorize this event type for better event type selection',
						name: 'tagIds',
						type: 'Custom',
						Component: () => (
							<TagAutocomplete
								value={selectedTagIds}
								onChange={handleTagChange}
								options={allTags}
								getOptionLabel={(o: Tag) => o.name}
								multiple={true}
								size="small"
								onReload={handleReloadTags}
								optionsLoading={tagsLoading}
							/>
						),
					},
					{
						rowLabel: 'Archival Period',
						rowDescription:
							'Period after which the event will be automatically archived, counting from the finish time of the event',
						name: 'archivalPeriod',
						type: 'DurationPicker',
					},
					{
						rowLabel: 'Expiry Warning',
						rowDescription: 'Send a warning email to the event admins when the event is about to expire',
						name: 'expiryWarning',
						type: 'Checkbox',
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

interface RequirementsTabProps {
	data: Type;
	onChange: (data: Type) => void;
	taskTemplates: TaskTemplatesApiResponse['data'];
	requirements: Requirement[];
	featureFlags?: FeatureFlagsSettings['settings'];
}

const VCA_GROUP_REQUIREMENT_KEYS = ['vca-checked', 'vca-checked-*', 'vca-checked-**', 'vca-checked-p'];

function RequirementsTab({ data, onChange, taskTemplates, requirements, featureFlags }: RequirementsTabProps) {

	const showSelfEmployedInfo = useMemo(() => {
		if (!featureFlags?.USES_VCA_SELF_EMPLOYED) return false;

		const mainReqs = data.requirements || [];
		const subReqs = data.requirementsSubcontractors || [];
		const hasVcaGroupRequirement =
			mainReqs.some((r) => VCA_GROUP_REQUIREMENT_KEYS.includes(r.key)) ||
			subReqs.some((r) => VCA_GROUP_REQUIREMENT_KEYS.includes(r.key));

		return hasVcaGroupRequirement;
	}, [data.requirements, data.requirementsSubcontractors, featureFlags?.USES_VCA_SELF_EMPLOYED]);

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Tasks',
						rowDescription:
							'Selected tasks will automatically be assigned to this type. The tasks can be reordered by dragging them to another position.',
						name: 'tasks',
						type: 'AutoCompleteByID',
						options: taskTemplates.map((t) => ({ ...t, id: t.slug })),
						getOptionLabel: (o: any) => o.name,
						multiple: true,
						draggable: true,
					},
					{
						rowLabel: `Requirements ${featureFlags?.USES_SUBCONTRACTING ? 'Main Event' : ''}`,
						rowDescription: `For this type to be valid ${featureFlags?.USES_SUBCONTRACTING ? 'for the main event' : ''}, the assigned requirements will be evaluated`,
						name: 'requirements',
						type: 'AutoComplete',
						options: requirements.map((t) => ({ ...t, id: t.key })),
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
					featureFlags?.USES_SUBCONTRACTING && {
						rowLabel: `Requirements Child Event`,
						rowDescription: `For this type to be valid for a child event, the assigned requirements will be evaluated`,
						name: 'requirementsSubcontractors',
						type: 'AutoComplete',
						options: requirements.map((t) => ({ ...t, id: t.key })),
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
			{showSelfEmployedInfo && (
				<Alert severity="info" className="mt-4">
					<strong>Self-employed (ZZP) companies:</strong> When the event&apos;s main contractor is a
					self-employed company, the VCA requirement will be validated against the user&apos;s documents
					(vca-user-vol or ssc-user-vol) instead of the company&apos;s documents.
				</Alert>
			)}
		</div>
	);
}

interface SiteAccessTabProps {
	data: Type;
	onChange: (data: Type) => void;
}

function SiteAccessTab({ data, onChange }: SiteAccessTabProps) {
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Access Management',
						rowDescription:
							'Manage access to the event either by assigning individual users, or when selecting company-based, all company users will receive access',
						name: 'access',
						type: 'AutoCompleteByID',
						options: [
							{ id: 'group', label: 'Company-based' },
							{ id: 'user', label: 'User-based' },
						],
						getOptionLabel: (o: any) => o.label,
						disableClearable: true,
					},
					{
						rowLabel: 'Allow Subcontracting',
						rowDescription: 'Enable the subcontractor management tools',
						name: 'allowChildGroups',
						type: 'Checkbox',
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

interface PermissionsTabProps {
	data: {
		possibleRoleIds?: string[];
		assignableByRoleIds?: string[];
		intersectPossibleRoleIdsWithZone?: boolean;
		[key: string]: any;
	};
	onChange: (_data: any) => void;
	roles: Role[];
	onValidationChange?: (_isValid: boolean) => void;
}

function PermissionsTab({ data, onChange, roles = [], onValidationChange }: PermissionsTabProps) {
	const [formKey, setFormKey] = useState(uuidv4());

	const missingParentRoles = useMemo(() => {
		if (!data.possibleRoleIds || data.possibleRoleIds.length === 0) return [];

		const selectedRoleIds = new Set(data.possibleRoleIds);
		const missing: Array<{ childRole: Role; parentRoles: Role[] }> = [];

		data.possibleRoleIds.forEach((roleId) => {
			const role = roles.find((r) => r.id === roleId);
			if (role && role.parentRoleIds && role.parentRoleIds.length > 0) {
				const hasParentSelected = role.parentRoleIds.some((parentId) => selectedRoleIds.has(parentId));
				if (!hasParentSelected) {
					const parentRoles = role.parentRoleIds
						.map((parentId) => roles.find((r) => r.id === parentId))
						.filter((r): r is Role => r !== undefined);
					missing.push({ childRole: role, parentRoles });
				}
			}
		});

		return missing;
	}, [data.possibleRoleIds, roles]);

	const hasValidRoleSelection = missingParentRoles.length === 0;

	useEffect(() => {
		onValidationChange?.(hasValidRoleSelection);
	}, [hasValidRoleSelection, onValidationChange]);

	const handleAddParentRole = (parentRoleId: string) => {
		const currentRoleIds = data.possibleRoleIds || [];
		if (!currentRoleIds.includes(parentRoleId)) {
			onChange({
				...data,
				possibleRoleIds: [...currentRoleIds, parentRoleId],
			});
			setFormKey(uuidv4());
		}
	};

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Assignable Roles',
						rowDescription: 'These roles will be available to be assigned to users of this type',
						name: 'possibleRoleIds',
						type: 'AutoCompleteByID',
						options: roles,
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
					missingParentRoles.length > 0 && {
						type: 'Custom',
						Component: () => (
							<MissingParentRolesAlert missingParentRoles={missingParentRoles} onAddParentRole={handleAddParentRole} />
						),
					},
					{
						rowLabel: 'Take zones into account',
						rowDescription:
							'Only roles linked to the assigned zone (and their parent roles, if applicable) can be assigned to users for this event type',
						name: 'intersectPossibleRoleIdsWithZone',
						type: 'Checkbox',
					},
					{
						rowLabel: 'Can be used by',
						rowDescription: 'Roles that will be allowed to use this event type',
						name: 'assignableByRoleIds',
						type: 'AutoCompleteByID',
						options: roles,
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form key={formKey} schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

interface TimingTabProps {
	data: Type;
	onChange: (data: Type) => void;
}

function TimingTab({ data, onChange }: TimingTabProps) {
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Enable Time Selection',
						rowDescription: 'Shows time selection fields',
						name: 'enableTimeSelection',
						type: 'Checkbox',
					},
					{
						rowLabel: 'Default Start Now',
						rowDescription: 'On creation, the event, start time will be set to the current time',
						name: 'defaultStartNow',
						type: 'Checkbox',
					},
					{
						rowLabel: 'Default Start Hour',
						rowDescription: 'Default start hour for the event',
						name: 'defaultStartHour',
						type: 'TextField',
						htmlType: 'number',
					},
					{
						rowLabel: 'Default Duration (hours)',
						rowDescription: 'Default duration for the event',
						name: 'defaultDurationHours',
						type: 'TextField',
						htmlType: 'number',
					},
					{
						rowLabel: 'Max Duration (days)',
						rowDescription: 'Maximum duration for the event. Will be ignored when Max Duration (hours) is set',
						name: 'maxDurationDays',
						type: 'TextField',
						htmlType: 'number',
					},
					{
						rowLabel: 'Max Duration (hours)',
						rowDescription: 'Maximum duration for the event',
						name: 'maxDurationHours',
						type: 'TextField',
						htmlType: 'number',
					},
					{
						rowLabel: 'Grace Period Before (minutes)',
						rowDescription: 'Allow visitors to check in this many minutes before the event starts. A site can cap this value.',
						name: 'allowMinutesPre',
						type: 'TextField',
						htmlType: 'number',
					},
					{
						rowLabel: 'Grace Period After (minutes)',
						rowDescription: 'Allow visitors to check in or remain on site this many minutes after the event ends. A site can cap this value.',
						name: 'allowMinutesPost',
						type: 'TextField',
						htmlType: 'number',
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

function EventTypeDetailPage() {
	const { typeId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags;

	const [typeData, setTypeData] = useState<(Type & { access?: string }) | null>(null);
	const [activeTab, setActiveTab] = useState(0);
	const [hasChanged, setChanged] = useState(false);
	const [isPermissionsValid, setIsPermissionsValid] = useState(true);
	const [isGeneralValid, setIsGeneralValid] = useState(true);

	useBreadcrumbLevel(0, 'Configuration', '/config/types/event');
	useBreadcrumbLevel(1, 'Event Types', '/config/types/event');
	useBreadcrumbLevel(2, typeData?.name || 'Event Type Details');

	const { data: type, isLoading: isTypeLoading } = useQuery({
		queryKey: ['type', 'event', typeId],
		queryFn: () => getTypeForModel('event', typeId),
	});

	const taskTemplatesQuery = constructQuery({ sort: 'name:asc', type: 'event,approval', noPagination: 'true' });
	const { data: taskTemplates = { data: [] } } = useQuery({
		queryKey: ['task-templates-event'],
		queryFn: () => getTaskTemplates(taskTemplatesQuery),
	});

	const formsQuery = constructQuery({ noPagination: 'true', type: 'event' });

	const { data: forms = { data: [] } } = useQuery({
		queryKey: ['forms-event'],
		queryFn: () => getForms(formsQuery),
	});

	const { data: requirements = [] } = useQuery({
		queryKey: ['requirements'],
		queryFn: getRequirements,
	});

	const { data: rolesResponse } = useQuery({
		queryKey: ['roles-all'],
		queryFn: getAllRoles,
	});

	const roles = rolesResponse?.data ?? [];

	const { data: tags = [], isLoading: isTagsLoading } = useQuery({
		queryKey: ['tags'],
		queryFn: getAllTags,
		enabled: !!featureFlags?.USES_TAGS,
	});

	useEffect(() => {
		if (type && !typeData) {
			setTypeData({
				...type,
				access: (type as any).inheritUsersFromGroups ? 'group' : 'user',
			});
		}
	}, [type, typeData]);

	const handleChange = (data: any) => {
		setChanged(true);
		setTypeData(data);
		setIsGeneralValid(!!data.name);
	};

	const revalidateTags = () => {
		queryClient.invalidateQueries({ queryKey: ['tags'] });
	};

	const saveChanges = async () => {
		if (!typeData) return;

		const data = {
			...typeData,
			inheritUsersFromGroups: typeData.access === 'group',
		};

		try {
			await clientRequest.put(
				`/api/types/event/${typeId}`,
				data.form === undefined
					? {
							...data,
							form: null,
						}
					: data,
			);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Event Type updated!',
			});
			setChanged(false);
			queryClient.invalidateQueries({ queryKey: ['type', 'event', typeId] });
			queryClient.invalidateQueries({ queryKey: ['types-event'] });
		} catch (err: any) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: err.status === 405 ? err.message : 'Failed to update Event Type.',
			});
		}
	};

	if (isTypeLoading || !typeData) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const tabs = [
		{
			label: 'General',
			el: (
				<GeneralTab
					data={typeData}
					onChange={handleChange}
					forms={forms.data}
					tags={tags}
					tagsLoading={isTagsLoading}
					revalidateTags={revalidateTags}
					featureFlags={featureFlags}
				/>
			),
		},
		{
			label: 'Requirements',
			el: (
				<RequirementsTab
					data={typeData}
					onChange={handleChange}
					taskTemplates={taskTemplates.data}
					requirements={requirements}
					featureFlags={featureFlags}
				/>
			),
		},
		{
			label: 'Site Access',
			el: <SiteAccessTab data={typeData} onChange={handleChange} />,
		},
		{
			label: 'Permissions',
			el: (
				<PermissionsTab
					data={typeData}
					onChange={handleChange}
					roles={roles}
					onValidationChange={setIsPermissionsValid}
				/>
			),
		},
		{
			label: 'Timing',
			el: <TimingTab data={typeData} onChange={handleChange} />,
		},
	];

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content className="bg-white">
					<Paper
						variant="outlined"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flexWrap: 'wrap',
							flexGrow: 1,
						}}
					>
						<Tabs
							value={activeTab}
							onChange={(i) => setActiveTab(i)}
							actions={
								<>
									<Button
										disabled={!hasChanged || !isPermissionsValid || !isGeneralValid}
										size="small"
										variant="outlined"
										onClick={saveChanges}
									>
										Save Changes
									</Button>
								</>
							}
						>
							<Tabs.TabList>
								{tabs.map((t, index) => (
									<Tabs.Tab key={t.label} value={index} label={t.label} />
								))}
							</Tabs.TabList>

							{tabs.map((tab, index) => (
								<Tabs.TabPanel selectedValue={activeTab} key={tab.label} value={index}>
									{typeData && tab.el}
								</Tabs.TabPanel>
							))}
						</Tabs>
					</Paper>
				</GenericDetailPage.Content>
			</GenericDetailPage>
		</>
	);
}
